import React, { useState } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { customerIdCardFileUrl, removeCustomerIdCard, uploadCustomerIdCard } from "../services/customerIdCardApi";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

/**
 * Where the customer adds their Aadhaar or another photo ID. The file input's ref comes from the
 * page, so the reminder's "Upload now" can open the picker straight away.
 */
export default function TrackingIdCards({ token, cards, onChange, inputRef }) {
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const upload = async (file) => {
    if (file.size > MAX_BYTES) {
      toast.error("The file must be under 10 MB. Please choose a smaller photo or PDF.");
      return;
    }
    setUploading(true);
    try {
      onChange(await uploadCustomerIdCard(token, file));
      toast.success("ID card uploaded. Thank you!");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not upload the ID card. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (card) => {
    if (!window.confirm(`Remove ${card.fileName}?`)) return;
    setRemovingId(card.id);
    try {
      onChange(await removeCustomerIdCard(token, card.id));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not remove the file. Please try again.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Card body className="public-tracking-info-card public-tracking-id-card" data-tour="id-cards">
      <div className="public-tracking-info-icon">ID</div>
      <div className="fw-semibold mb-1">Your ID Card</div>
      <div className="small mb-2 public-tracking-id-help">
        Upload your Aadhaar card or another government photo ID. You can add the front and back, or one for
        each traveller. A masked Aadhaar (only the last 4 digits shown), from the UIDAI website, is fine too.
      </div>
      {cards.length ? (
        <div className="d-flex flex-column gap-2 mb-2">
          {cards.map((card) => (
            <div key={card.id} className="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded px-2 py-1 bg-white">
              <span className="small fw-semibold text-break">
                <i className="bi bi-check-circle-fill text-success me-1" aria-hidden="true" />
                {card.fileName}
              </span>
              <span className="d-flex gap-2">
                <Button as="a" size="sm" variant="outline-secondary" href={customerIdCardFileUrl(token, card.id)} target="_blank" rel="noopener noreferrer">
                  View
                </Button>
                <Button size="sm" variant="outline-danger" disabled={removingId === card.id} onClick={() => remove(card)}>
                  {removingId === card.id ? <Spinner size="sm" animation="border" /> : "Remove"}
                </Button>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="small fw-semibold text-danger mb-2">Not uploaded yet</div>
      )}
      <label className={`btn btn-primary btn-sm mb-0${uploading ? " disabled" : ""}`}>
        {uploading ? (
          <><Spinner size="sm" animation="border" className="me-2" />Uploading...</>
        ) : (
          <><i className="bi bi-upload me-2" aria-hidden="true" />{cards.length ? "Add Another" : "Upload ID Card"}</>
        )}
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={ACCEPT}
          disabled={uploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Cleared so choosing the same file again still uploads.
            event.target.value = "";
            if (file) upload(file);
          }}
        />
      </label>
      <div className="small text-muted mt-1">Photo or PDF, up to 10 MB.</div>
    </Card>
  );
}
